"use client";

import { useMemo, type ReactNode } from "react";
import { useTranslations } from "next-intl";
import {
  IconCheck,
  IconDental,
  IconInfoCircle,
  IconPhone,
  IconPlaneArrival,
  IconPlaneDeparture,
} from "@tabler/icons-react";

import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { useAuthStore } from "@/stores/auth-store";
import { calendarPreset } from "@/lib/appointment-presets";
import { getCategoryLabel } from "@/lib/appointment-categories";
import { STATUS_HEX } from "@/lib/appointment-status";
import { ARRIVAL_VISIT_FILL, arrivalVisitBucket } from "@/lib/calendar-events";

import type { CalendarEvent, Mode } from "./calendar-types";
import { CHECKIN_DOT, MEDICAL_DOT, presentBrands, presentDoctors } from "./event-style";

/**
 * One legend row: a swatch/glyph + its label. The row, section and key-cap
 * pieces are shared with the desk board's legend (NeedsBookingLegend) so the
 * two legends read as one design.
 */
export function LegendRow({ swatch, label }: { swatch: ReactNode; label: string }) {
  return (
    <div className="flex items-center gap-2 text-xs">
      <span className="flex size-4 shrink-0 items-center justify-center">{swatch}</span>
      <span className="text-muted-foreground">{label}</span>
    </div>
  );
}

export function LegendSection({ title, children }: { title: string; children: ReactNode }) {
  return (
    <div className="space-y-1.5">
      <p className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">{title}</p>
      {children}
    </div>
  );
}

export function Kbd({ children }: { children: ReactNode }) {
  return (
    <kbd className="rounded border border-border bg-muted px-1 font-mono text-[10px] text-foreground">
      {children}
    </kbd>
  );
}

/**
 * Appointments rebuild 87: the calendar legend, always available (no flag),
 * decoding every signal a chip can carry — status, category band, doctor
 * colour, medical alert, check-in dots, blockout shading, travel — plus the
 * keyboard shortcuts. Lists only what is active in the current view.
 */
export function CalendarLegend({
  mode,
  events,
  availableModes,
}: {
  mode: Mode;
  events: CalendarEvent[];
  availableModes: Mode[];
}) {
  const t = useTranslations("calendar");
  const tAppointments = useTranslations("appointments");

  const typesEnabled = useAuthStore(
    (s) => s.user?.feature_flags?.typed_events_enabled === true
  );
  const doctorColorsEnabled = useAuthStore((s) =>
    calendarPreset(s.user?.appointment_presets, "doctor_colors")
  );
  const travelEnabled = useAuthStore((s) => s.user?.feature_flags?.travel_enabled !== false);
  const simpleClinicMode = useAuthStore((s) => s.user?.feature_flags?.simple_clinic_mode ?? false);

  const brands = useMemo(() => presentBrands(events), [events]);
  // Dentour 261009/261015: the feed numbers arrival legs only on a clinic
  // whose planes wear visit colours, so a numbered arrival in view is the
  // signal — no preset read needed, and the legend never claims colours the
  // grid is not showing.
  const visitColoured = useMemo(
    () =>
      events.some(
        (e) => arrivalVisitBucket(e.eventType === "arrival", e.feedMeta?.visit_ordinal) !== null
      ),
    [events]
  );
  const doctors = useMemo(
    () => (doctorColorsEnabled ? presentDoctors(events) : []),
    [doctorColorsEnabled, events]
  );
  const showBlockout = mode === "doctors" || mode === "rooms";
  const gridViews = availableModes.filter((m) => m === "day" || m === "week" || m === "month");

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button variant="outline" size="icon" aria-label={t("legend.trigger")} title={t("legend.trigger")}>
          <IconInfoCircle className="h-4 w-4" />
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-64">
        <div className="space-y-3">
          <p className="text-sm font-semibold">{t("legend.title")}</p>

          {typesEnabled && (
            <LegendSection title={t("legend.typesTitle")}>
              <LegendRow
                swatch={
                  <span className="flex size-4 items-center justify-center rounded-sm bg-slate-600 text-white">
                    <IconDental className="size-2.5" />
                  </span>
                }
                label={t("legend.clinicVisit")}
              />
              <LegendRow
                swatch={
                  <span className="flex size-4 items-center justify-center rounded-sm border border-slate-500 bg-slate-500/15 text-foreground">
                    <IconPhone className="size-2.5" />
                  </span>
                }
                label={t("legend.leadCall")}
              />
            </LegendSection>
          )}

          <LegendSection title={t("legend.statusTitle")}>
            <LegendRow
              swatch={<span className="size-4 rounded-sm" style={{ backgroundColor: STATUS_HEX.scheduled }} />}
              label={t("legend.scheduled")}
            />
            <LegendRow
              swatch={
                <span
                  className="flex size-4 items-center justify-center rounded-sm text-white"
                  style={{ backgroundColor: STATUS_HEX.scheduled }}
                >
                  <IconCheck className="size-2.5" />
                </span>
              }
              label={t("legend.completed")}
            />
            <LegendRow
              swatch={
                <span
                  className="size-4 rounded-sm opacity-50"
                  style={{ backgroundColor: STATUS_HEX.scheduled }}
                />
              }
              label={t("legend.cancelled")}
            />
            <LegendRow
              swatch={
                <span
                  className="size-4 rounded-sm border-2 border-dashed"
                  style={{ borderColor: STATUS_HEX.no_show }}
                />
              }
              label={t("legend.noShow")}
            />
          </LegendSection>

          {brands.length > 0 && (
            <LegendSection title={t("legend.categoriesTitle")}>
              {brands.map(({ category, color }) => (
                <LegendRow
                  key={category}
                  swatch={
                    <span
                      className="size-4 rounded-sm bg-slate-600"
                      style={{ boxShadow: `inset 3px 0 0 0 ${color}` }}
                    />
                  }
                  label={getCategoryLabel(tAppointments, category)}
                />
              ))}
            </LegendSection>
          )}

          {doctors.length > 0 && (
            <LegendSection title={t("legend.doctorsTitle")}>
              {doctors.map(({ id, name, color }) => (
                <LegendRow
                  key={id}
                  swatch={<span className="size-4 rounded-sm ring-1 ring-black/10" style={{ backgroundColor: color }} />}
                  label={name}
                />
              ))}
            </LegendSection>
          )}

          <LegendSection title={t("legend.markersTitle")}>
            <LegendRow
              swatch={<span className={`size-2.5 rounded-full ${MEDICAL_DOT}`} />}
              label={t("legend.medicalAlert")}
            />
            {!simpleClinicMode && (
              <>
                <LegendRow
                  swatch={<span className={`size-2.5 rounded-full ${CHECKIN_DOT.arrived}`} />}
                  label={t("legend.arrived")}
                />
                <LegendRow
                  swatch={<span className={`size-2.5 rounded-full ${CHECKIN_DOT.seated}`} />}
                  label={t("legend.seated")}
                />
              </>
            )}
            {showBlockout && (
              <LegendRow
                swatch={
                  <span className="size-4 rounded-sm bg-muted [background-image:repeating-linear-gradient(135deg,transparent,transparent_3px,var(--border)_3px,var(--border)_4px)]" />
                }
                label={t("legend.blockout")}
              />
            )}
          </LegendSection>

          {travelEnabled && (
            <LegendSection title={t("legend.travelTitle")}>
              {visitColoured ? (
                // Dentour 261015 — arrivals wear the visit colour: white
                // first, green second, brown returning.
                <>
                  <LegendRow
                    swatch={
                      <span className="flex size-4 items-center justify-center rounded-sm bg-white text-neutral-900 ring-1 ring-neutral-300">
                        <IconPlaneArrival className="size-2.5" />
                      </span>
                    }
                    label={t("legend.arrivalFirst")}
                  />
                  <LegendRow
                    swatch={
                      <span className="flex size-4 items-center justify-center rounded-sm bg-green-600 text-white">
                        <IconPlaneArrival className="size-2.5" />
                      </span>
                    }
                    label={t("legend.arrivalSecond")}
                  />
                  <LegendRow
                    swatch={
                      <span
                        className="flex size-4 items-center justify-center rounded-sm text-white"
                        style={{ backgroundColor: ARRIVAL_VISIT_FILL[3] }}
                      >
                        <IconPlaneArrival className="size-2.5" />
                      </span>
                    }
                    label={t("legend.arrivalReturn")}
                  />
                </>
              ) : (
                <LegendRow
                  swatch={
                    <span className="flex size-4 items-center justify-center rounded-sm bg-green-600 text-white">
                      <IconPlaneArrival className="size-2.5" />
                    </span>
                  }
                  label={t("legend.arrival")}
                />
              )}
              <LegendRow
                swatch={
                  <span className="flex size-4 items-center justify-center rounded-sm bg-orange-500 text-white">
                    <IconPlaneDeparture className="size-2.5" />
                  </span>
                }
                label={t("legend.departure")}
              />
            </LegendSection>
          )}

          <LegendSection title={t("legend.keyboardTitle")}>
            <div className="flex items-center gap-2 text-xs text-muted-foreground">
              <Kbd>T</Kbd>
              <span>{t("legend.keyboardToday")}</span>
            </div>
            <div className="flex items-center gap-2 text-xs text-muted-foreground">
              <Kbd>←</Kbd>
              <Kbd>→</Kbd>
              <span>{t("legend.keyboardPeriod")}</span>
            </div>
            {gridViews.length > 1 && (
              <div className="flex items-center gap-2 text-xs text-muted-foreground">
                {gridViews.map((m) => (
                  <Kbd key={m}>{m[0].toUpperCase()}</Kbd>
                ))}
                <span>{t("legend.keyboardViews")}</span>
              </div>
            )}
            <div className="flex items-center gap-2 text-xs text-muted-foreground">
              <Kbd>↑</Kbd>
              <Kbd>↓</Kbd>
              <span>{t("legend.keyboardChips")}</span>
            </div>
          </LegendSection>
        </div>
      </PopoverContent>
    </Popover>
  );
}
